/**
 * ADR-0029: background capture on the phone. The native OmniCapture plugin records calls,
 * notifications and location with Omni closed, queues them and uploads them with a capture
 * token for one app (Loggers). This side gets that token and passes on what the app asks.
 */
import { registerPlugin } from "@capacitor/core";
import type { CaptureSettingsTarget, CaptureSource, CaptureStatus, LocationRecordingStatus, LocationStartError } from "@omni/sdk/protocol";
import { mayFlushCapture, safeUploadError } from "@omni/sdk/upload";

import { api, apiBase } from "./api";
import { isNative } from "./platform";
import { load, save } from "./storage";

type NativeStatus = Omit<CaptureStatus, "sources"> & {
  configured: boolean;
  sources: { source: CaptureSource; enabled: boolean; app: string | null; permission: CaptureStatus["sources"][number]["permission"] }[];
};

interface OmniCapturePlugin {
  configure(o: { server: string; token: string }): Promise<NativeStatus>;
  status(): Promise<NativeStatus>;
  setSource(o: { source: CaptureSource; enabled: boolean; appId: string }): Promise<NativeStatus>;
  currentPosition(): Promise<Position>;
  flush(): Promise<NativeStatus>;
  openSettings(o: { source: CaptureSettingsTarget }): Promise<void>;
  reset(): Promise<NativeStatus>;
  notificationsSeen(o: { id: number }): Promise<void>;
}

const Native = registerPlugin<OmniCapturePlugin>("OmniCapture");
const SOURCES: CaptureSource[] = ["calls", "notifications", "location"];

/** A stable id for this phone (named in the capture token, and in Loggers). */
function deviceId(): string {
  let id = load<string | null>("omni.deviceId", null);
  if (!id) {
    id = `android-${crypto.randomUUID().slice(0, 12)}`;
    save("omni.deviceId", id);
  }
  return id;
}

/** What one app sees: a source is "on" for it only if this phone records it for that app. */
function view(n: NativeStatus | null, appId: string, scopes: string[]): CaptureStatus {
  const mine = !!n && n.configured && n.sources.some((s) => s.enabled && s.app === appId);
  return {
    available: isNative,
    sources: SOURCES.map((source) => {
      const s = n?.sources.find((x) => x.source === source);
      return {
        source,
        enabled: !!n?.configured && !!s?.enabled && s.app === appId,
        recordedFor: s?.enabled && s.app && s.app !== appId ? s.app : null,
        allowed: scopes.includes(`device:${source}`),
        permission: isNative ? s?.permission ?? "prompt" : "unavailable",
      };
    }),
    queued: mine ? n!.queued : 0,
    lastUpload: mine ? n!.lastUpload : null,
    lastError: mine ? safeUploadError(n!.lastError) : null, // PRD-0004 S-6: fixed texts only
    batteryUnrestricted: !!n?.batteryUnrestricted,
    location: n?.location && scopes.some((x) => x.startsWith("device:")) ? locationView(n, appId) : undefined,
    // PRD-0004 FR-10 / SR-3: upload details, like `queued`, only to an app this phone records for
    upload: mine && n!.upload ? { ...n!.upload, lastError: safeUploadError(n!.upload.lastError) } : undefined,
  };
}

/**
 * PRD-0003: what blocks location recording is shown to apps on this phone with a `device:*` scope
 * (so Loggers can point to it, S-1); when the last fix was taken and why the service couldn't
 * start only to the app it's recorded for (SR-4).
 */
function locationView(n: NativeStatus, appId: string): LocationRecordingStatus {
  const l = n.location!;
  const src = n.sources.find((x) => x.source === "location");
  const mine = n.configured && !!src?.enabled && src.app === appId;
  return {
    running: !!l.running,
    lastFixAt: mine ? l.lastFixAt ?? null : null,
    blockedBy: l.blockedBy ?? null,
    blockers: l.blockers ?? [],
    intervalMin: l.intervalMin ?? 5,
    samsung: !!l.samsung,
    startError: mine ? startErrorOf(l.startError) : null,
  };
}

const START_ERRORS: readonly LocationStartError[] = ["permission", "background_permission", "background_start", "other"];
/** Only the native category passes; anything else (an older phone app's raw text) becomes "other". */
function startErrorOf(e: unknown): LocationStartError | null {
  if (!e) return null;
  return START_ERRORS.includes(e as LocationStartError) ? (e as LocationStartError) : "other";
}

const SETTINGS: readonly CaptureSettingsTarget[] = ["calls", "notifications", "location", "battery", "location_off"];
/** SR-6: only these fixed targets reach the phone; it opens fixed Android screens for Omni. */
export const isSettingsTarget = (s: unknown): s is CaptureSettingsTarget => SETTINGS.includes(s as CaptureSettingsTarget);

export async function captureStatus(appId: string, scopes: string[]): Promise<CaptureStatus> {
  return view(isNative ? await Native.status() : null, appId, scopes);
}

/**
 * Make sure the phone has a token for this server (one per phone, for all apps): it uploads
 * what it records (ADR-0030) and fetches notifications with Omni closed (ADR-0031).
 */
export async function ensureDevice(): Promise<void> {
  if (!isNative) return;
  const n = await Native.status();
  const key = `${apiBase()}|v2`;
  if (n.configured && load<string | null>("omni.captureFor", null) === key) return;
  const t = await api<{ token: string }>("/api/device/token", { method: "POST", body: { device: deviceId() } });
  await Native.configure({ server: apiBase(), token: t.token });
  save("omni.captureFor", key);
  // Background notifications start from now, not from the whole inbox
  const newest = await api<{ items: { id: number }[] }>("/api/notifications", { query: { limit: 1 } }).catch(() => null);
  await Native.notificationsSeen({ id: newest?.items[0]?.id ?? 0 });
}

export async function captureSet(appId: string, scopes: string[], source: CaptureSource, enabled: boolean): Promise<CaptureStatus> {
  if (!isNative) return captureStatus(appId, scopes);
  if (enabled) await ensureDevice();
  const n = await Native.status();
  if (!n.configured) return view(n, appId, scopes);
  // Turning off only applies to the app it's recorded for
  const s = n.sources.find((x) => x.source === source);
  if (!enabled && s?.app && s.app !== appId) return view(n, appId, scopes);
  return view(await Native.setSource({ source, enabled, appId }), appId, scopes);
}

/**
 * "Upload now". PRD-0004 S-3: it skips the upload backoff, so only an app the phone records a source for (with
 * that source's `device:*` permission) can start it; for any other app it only returns the status.
 */
export async function captureFlush(appId: string, scopes: string[]): Promise<CaptureStatus> {
  if (!isNative) return view(null, appId, scopes);
  const n = await Native.status();
  if (!n.configured || !mayFlushCapture(appId, scopes, n.sources)) return view(n, appId, scopes);
  return view(await Native.flush(), appId, scopes);
}

export async function captureSettings(source: CaptureSettingsTarget): Promise<void> {
  if (isNative && isSettingsTarget(source)) await Native.openSettings({ source });
}

export interface Position { lat: number; lon: number; accuracy: number | null; at: string }

/** Where the device is now: the phone's location, or the browser's on the web. */
export async function currentPosition(): Promise<Position> {
  if (isNative) return Native.currentPosition();
  if (!navigator.geolocation) throw new Error("This browser can't tell where you are");
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy ?? null, at: new Date(p.timestamp).toISOString() }),
      (e) => reject(new Error(e.code === e.PERMISSION_DENIED ? "Location is blocked for Omni in this browser" : "Couldn't get your location")),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 30_000 },
    ),
  );
}

/** Sign-out: stop recording and forget the token. */
export async function captureReset(): Promise<void> {
  save("omni.captureFor", null);
  if (isNative) await Native.reset().catch(() => undefined);
}
